import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCompteursMessagerie } from '../../lib/api/messagerieApi';
import './Messagerie.css';

/**
 * LA BOÎTE AUX LETTRES, EN BAS À GAUCHE : pour les administrateurs, un bouton
 * toujours visible qui mène à Administration › Mails ; un badge qui compte les
 * non lus de la boîte du support ; et un effet « respirant » quand un courriel
 * vient d'arriver. À côté, le bouclier mène à l'administration.
 *
 * LE COMPTE vient des compteurs de la boîte, lus à l'arrivée, au retour sur
 * l'onglet et toutes les deux minutes ; il est mis à jour tout de suite quand
 * un courriel arrive (`sj:nouveau-mail`, voir NotificationsMail) ou quand la
 * messagerie ouverte change ses non lus (`sj:non-lus`, voir Messagerie).
 *
 * LA RESPIRATION dure jusqu'au clic sur le bouton, ou jusqu'à ce qu'il ne reste
 * plus rien à lire.
 *
 * CACHÉ LÀ OÙ IL GÊNERAIT : sur les pages de connexion et de paiement, et sur
 * téléphone quand un message ouvert occupe tout l'écran (voir la feuille de style).
 */
const INTERVALLE_MS = 120000;
const PAGES_SANS_BOUTON = [
  /^\/connexion/, /^\/inscription/, /^\/mot-de-passe-oublie/, /^\/reinitialiser-mdp/, /^\/payment-success/,
];

function IconeMail() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="M3.5 7l8.5 6 8.5-6" />
    </svg>
  );
}

function IconeBouclier() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l7 3v5.5c0 4.4-3 8.2-7 9.5-4-1.3-7-5.1-7-9.5V6l7-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

export default function BoutonMails() {
  const naviguer = useNavigate();
  const { pathname } = useLocation();
  const [nonLus, setNonLus] = useState(null);
  const [nouveau, setNouveau] = useState(false);

  const lire = useCallback(async () => {
    try {
      const { data } = await getCompteursMessagerie();
      setNonLus(typeof data?.nonLus === 'number' && !data?.erreur ? data.nonLus : null);
    } catch {
      // Boîte non configurée ou injoignable : le bouton reste, sans badge.
      setNonLus(null);
    }
  }, []);

  useEffect(() => {
    lire();
    const minuteur = setInterval(() => { if (document.visibilityState !== 'hidden') lire(); }, INTERVALLE_MS);
    const auRetour = () => { if (document.visibilityState === 'visible') lire(); };
    document.addEventListener('visibilitychange', auRetour);
    return () => {
      clearInterval(minuteur);
      document.removeEventListener('visibilitychange', auRetour);
    };
  }, [lire]);

  useEffect(() => {
    const arrive = (e) => {
      if (typeof e.detail?.nonLus === 'number') setNonLus(e.detail.nonLus);
      if ((e.detail?.messages ?? []).length > 0) setNouveau(true);
    };
    const change = (e) => {
      if (typeof e.detail?.nonLus !== 'number') return;
      setNonLus(e.detail.nonLus);
      if (e.detail.nonLus === 0) setNouveau(false);
    };
    window.addEventListener('sj:nouveau-mail', arrive);
    window.addEventListener('sj:non-lus', change);
    return () => {
      window.removeEventListener('sj:nouveau-mail', arrive);
      window.removeEventListener('sj:non-lus', change);
    };
  }, []);

  if (PAGES_SANS_BOUTON.some((motif) => motif.test(pathname))) return null;

  const libelle = nonLus > 0
    ? `Mails du support — ${nonLus} non lu${nonLus > 1 ? 's' : ''}`
    : 'Mails du support';

  return (
    <div className="raccourcis-admin">
      <button
        type="button"
        className={`bouton-mails${nouveau ? ' est-nouveau' : ''}`}
        aria-label={libelle}
        title={libelle}
        onClick={() => {
          setNouveau(false);
          naviguer('/admin?onglet=mails');
        }}
      >
        <IconeMail />
        {nonLus > 0 && (
          <span className="bouton-mails__badge" aria-hidden="true">{nonLus > 99 ? '99+' : nonLus}</span>
        )}
      </button>

      {/* LE BOUCLIER : l'administration, d'un clic. */}
      <button
        type="button"
        className="bouton-mails bouton-mails--admin"
        aria-label="Administration"
        title="Administration"
        onClick={() => naviguer('/admin')}
      >
        <IconeBouclier />
      </button>
    </div>
  );
}
